/**
 * Share and send dialog (README E). "Share from my device" builds wa.me,
 * sms: and mailto: links from the message the server renders, logs every
 * share as SHARED_MANUAL and previews what lands on the customer's phone.
 * "Send by Netlise" (paid sending) is Phase 4 and shows disabled.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import InvoiceRepository, {
  InvoiceListItem, ManualChannel, ShareMessage,
} from '../../../internals/repository/InvoiceRepository';
import { errorText } from '../detailModel';
import { date, money } from '../format';
import {
  isEmail, isSriLankaLandline, mailtoUrl, smsSegments, smsUrl, toE164, waUrl, withoutLink,
} from '../shareLinks';
import { useInvoiceSettings, useSelectedShop } from '../useInvoicing';
import { safeAccent } from './InvoicePreview';
import { ErrorBanner } from './VoidModal';

/** An InvoiceDoc or a list row: both carry these. */
export type ShareableDoc = Pick<InvoiceListItem, 'docId' | 'number' | 'displayStatus'> & {
  docType?: InvoiceListItem['docType'];
  customer: { name?: string; companyName?: string; phoneE164?: string; email?: string };
  shareUrl?: string;
  totalMinor?: number;
  balanceMinor?: number;
  dueDate?: string;
  validUntil?: string;
  currency?: string;
};

type Lang = 'en' | 'si' | 'ta';
type Pv = 'wa' | 'sms' | 'email';

const LANGS: { value: Lang; label: string }[] = [
  { value: 'en', label: 'English' }, { value: 'si', label: 'සිංහල' }, { value: 'ta', label: 'தமிழ்' },
];
const TEMPLATES = [
  { value: 'invoice_issued', label: 'Invoice issued' },
  { value: 'invoice_reminder', label: 'Payment reminder' },
  { value: 'invoice_overdue', label: 'Overdue notice' },
  { value: 'payment_received', label: 'Payment received' },
  { value: 'quotation_sent', label: 'Quotation' },
];
const PREVIEWS: { value: Pv; label: string }[] = [
  { value: 'wa', label: 'WhatsApp' }, { value: 'sms', label: 'SMS' }, { value: 'email', label: 'Email' },
];
const CH_BG = { wa: '#128C5A', sms: '#0C8FD4', email: '#4E45E4', copy: '#6A6A76', qr: '#131318' };
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const SHARE_ERRORS = { invalid_state: 'Issue the document before sharing it.' };

function useViewportWidth() {
  const [w, setW] = useState(() => window.innerWidth);
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return w;
}

const isTouch = () => !!window.matchMedia?.('(pointer: coarse)').matches;

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  }
}

/** react-qr-code SVG → white-padded PNG. */
async function svgToPng(svg: SVGSVGElement, size = 512, pad = 32): Promise<Blob> {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(size));
  clone.setAttribute('height', String(size));
  const src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(clone))}`;
  const img = new Image();
  await new Promise<void>((resolve, reject) => { img.onload = () => resolve(); img.onerror = reject; img.src = src; });
  const canvas = document.createElement('canvas');
  canvas.width = size + pad * 2;
  canvas.height = size + pad * 2;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, pad, pad, size, size);
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png'));
}

const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || 'N';

export default function ShareDialog(props: {
  open: boolean;
  onClose: () => void;
  shopId: string;
  docId: string;
  document: ShareableDoc;
  onShared?: () => void;
}) {
  if (!props.open) return null;
  return <ShareDialogBody {...props} />;
}

function ShareDialogBody({
  onClose, shopId, docId, document: doc, onShared,
}: {
  onClose: () => void;
  shopId: string;
  docId: string;
  document: ShareableDoc;
  onShared?: () => void;
}) {
  const shop = useSelectedShop();
  const { settings } = useInvoiceSettings(shopId);
  const shopName: string = shop?.shopName || 'Your shop';
  const accent = safeAccent((settings as any)?.branding?.accentColor);
  const vw = useViewportWidth();
  const wide = vw >= 1100;

  const [lang, setLang] = useState<Lang | null>(null);
  const [tpl, setTpl] = useState<string | null>(null);
  const [base, setBase] = useState<ShareMessage | null>(null);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [phone, setPhone] = useState(doc.customer?.phoneE164 || '');
  const [email, setEmail] = useState(doc.customer?.email || '');
  const [pv, setPv] = useState<Pv>('wa');
  const [emailMsg, setEmailMsg] = useState<{ key: string; m: ShareMessage } | null>(null);
  const [smsQr, setSmsQr] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const req = useRef(0);
  const touchedTo = useRef(false);
  const ref = useRef<HTMLDivElement>(null);
  const qrRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const load = useCallback((language?: Lang, templateKey?: string) => {
    const id = ++req.current;
    setLoading(true);
    setError(null);
    InvoiceRepository.shareMessage(shopId, docId, { channel: 'MANUAL_WHATSAPP', language, templateKey })
      .then((m) => {
        if (id !== req.current) return;
        setBase(m);
        setMsg(m.text);
        setLang(m.language);
        setTpl(m.templateKey);
        if (!touchedTo.current) {
          if (m.to?.phoneE164) setPhone(m.to.phoneE164);
          if (m.to?.email) setEmail(m.to.email);
        }
      })
      .catch((e) => { if (id === req.current) setError(errorText(e, SHARE_ERRORS)); })
      .finally(() => { if (id === req.current) setLoading(false); });
  }, [shopId, docId]);

  useEffect(() => { load(); }, [load]);

  // The email preview and the email app use the EMAIL template's subject.
  const emailKey = `${lang}|${tpl}`;
  const fetchEmail = useCallback(async (): Promise<ShareMessage | null> => {
    if (!lang || !tpl) return null;
    if (emailMsg?.key === emailKey) return emailMsg.m;
    try {
      const m = await InvoiceRepository.shareMessage(shopId, docId, { channel: 'MANUAL_EMAIL', language: lang, templateKey: tpl });
      setEmailMsg({ key: emailKey, m });
      return m;
    } catch {
      return null;
    }
  }, [shopId, docId, lang, tpl, emailKey, emailMsg]);

  useEffect(() => { if (pv === 'email') fetchEmail(); }, [pv, fetchEmail]);

  // Focus in on open, back out on close; Escape closes; Tab stays inside.
  useEffect(() => {
    const back = window.document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('[role="radio"][aria-checked="true"], textarea, button')?.focus();
    return () => back?.focus?.();
  }, []);
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
    if (e.key !== 'Tab' || !ref.current) return;
    const items = Array.from(ref.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (!items.length) return;
    const [first, last] = [items[0], items[items.length - 1]];
    if (e.shiftKey && window.document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && window.document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  const link = base?.link || doc.shareUrl || '';
  const phoneE164 = toE164(phone);
  const phoneBad = !!phone.trim() && !phoneE164;
  const emailBad = !!email.trim() && !isEmail(email);
  const edited = !!base && msg !== base.text;
  const ready = !!base && !loading;
  const isQuote = doc.docType === 'QUOTATION';
  const number = doc.number || 'document';
  const amountMinor = isQuote ? doc.totalMinor : Math.max(0, doc.balanceMinor ?? doc.totalMinor ?? 0);
  const currency = doc.currency || shop?.currency || 'LKR';
  const who = [doc.customer?.companyName, doc.customer?.name].filter(Boolean).join(' · ');
  const sms = smsSegments(msg);
  const emailSubject = emailMsg?.key === emailKey ? emailMsg.m.subject : undefined;

  const log = (channel: ManualChannel, text: string) => {
    setToast(text);
    InvoiceRepository.shareEvent(shopId, docId, channel)
      .then(() => onShared?.())
      .catch(() => setToast(`${text} · could not be logged on the timeline`));
  };

  const shareWhatsApp = () => {
    window.open(waUrl(phoneE164, msg), '_blank', 'noopener,noreferrer');
    log('MANUAL_WHATSAPP', 'Shared on WhatsApp');
  };
  const shareSms = () => {
    setPv('sms');
    if (isTouch()) {
      window.location.href = smsUrl(phoneE164, msg);
      log('MANUAL_SMS', 'Shared by SMS');
    } else {
      setSmsQr(true);
      log('MANUAL_SMS', 'Scan the code to text from your phone');
    }
  };
  const shareEmail = async () => {
    const m = await fetchEmail();
    const subject = m?.subject || `${isQuote ? 'Quotation' : 'Invoice'} ${number} from ${shopName}`;
    window.location.href = mailtoUrl(email, subject, msg);
    log('MANUAL_EMAIL', 'Your email app opened with the message');
  };
  const copyMessage = async () => {
    if (await copyText(msg)) log('COPY_MESSAGE', 'Message copied');
    else setToast('Could not copy. Select the message and copy it.');
  };
  const copyLink = async () => {
    if (await copyText(link)) log('COPY_LINK', 'Link copied');
    else setToast('Could not copy. Select the link and copy it.');
  };
  const downloadQr = async () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    try {
      const blob = await svgToPng(svg);
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = `${number}-qr.png`;
      window.document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      log('QR_DOWNLOAD', `${number}-qr.png downloaded`);
    } catch {
      setToast('The QR code could not be created. Try again.');
    }
  };

  const actions: { key: string; label: string; glyph: string; bg: string; run: () => void; on?: boolean }[] = [
    { key: 'wa', label: 'Share on WhatsApp', glyph: 'W', bg: CH_BG.wa, run: shareWhatsApp },
    { key: 'sms', label: 'Share by SMS', glyph: 'S', bg: CH_BG.sms, run: shareSms, on: smsQr },
    { key: 'email', label: 'Share by email app', glyph: 'E', bg: CH_BG.email, run: shareEmail },
    { key: 'copy', label: 'Copy message', glyph: '⧉', bg: CH_BG.copy, run: copyMessage },
    { key: 'qr', label: 'Download QR', glyph: 'QR', bg: CH_BG.qr, run: downloadQr },
  ];

  const labelCls = 'flex flex-col gap-1.5 text-[13px] font-semibold text-text2';

  return (
    <div className="nl-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-title"
        onKeyDown={onKey}
        className="nl-modal flex flex-col"
        style={{ maxWidth: wide ? 1120 : 760 }}
      >
        <div className="flex items-start justify-between gap-3 px-6 pt-5">
          <div className="flex min-w-0 flex-col gap-[3px]">
            <h2 id="share-title" className="m-0 text-[20px] font-semibold text-text1">Share {number}</h2>
            <p className="m-0 text-[13px] text-text2">
              {who || 'Customer'}
              {amountMinor != null && ` · ${isQuote ? 'Quotation total' : 'Balance'} `}
              {amountMinor != null && <span className="nl-num">{money(amountMinor, currency)}</span>}
            </p>
          </div>
          <button type="button" aria-label="Close" className="nl-btn nl-btn--ghost nl-btn--sm !w-8 !px-0 text-[18px]" onClick={onClose}>×</button>
        </div>

        <div role="tablist" className="mt-3.5 flex gap-1 border-b border-borderc px-6">
          <button
            type="button"
            role="tab"
            aria-selected="false"
            aria-disabled="true"
            disabled
            title="Arrives in a later release"
            className="-mb-px flex cursor-not-allowed items-baseline gap-2 border-b-2 border-transparent px-1 py-2.5 text-[14px] font-semibold text-text3"
          >
            Send by Netlise
            <span className="text-[11px] font-medium">Arrives in a later release</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected="true"
            className="-mb-px ml-3 border-b-2 border-brand px-1 py-2.5 text-[14px] font-semibold text-brand"
          >
            Share from my device
          </button>
        </div>

        <div className="grid min-h-[520px]" style={{ gridTemplateColumns: wide ? 'minmax(0,1fr) 380px' : 'minmax(0,1fr)' }}>
          <div role="tabpanel" className="flex min-w-0 flex-col gap-4 px-6 py-5">
            <span className="text-[13px] text-text2">Send from your own WhatsApp, SMS or email. No credits are used.</span>
            <ErrorBanner text={error}>
              {' '}
              <button type="button" className="font-semibold underline" onClick={() => load(lang ?? undefined, tpl ?? undefined)}>Try again</button>
            </ErrorBanner>

            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))' }}>
              <div className={labelCls}>
                <span id="share-lang">Language</span>
                <div role="radiogroup" aria-labelledby="share-lang" className="flex gap-0.5 rounded-lg border border-borderc bg-surface2 p-[3px]">
                  {LANGS.map((l) => {
                    const on = lang === l.value;
                    return (
                      <button
                        key={l.value}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        disabled={loading && !base}
                        onClick={() => { if (!on) load(l.value, tpl ?? undefined); }}
                        className={`h-[30px] flex-1 rounded-md text-[13px] font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand ${on ? 'bg-surface text-text1' : 'text-text2'}`}
                        style={on ? { boxShadow: '0 1px 2px rgba(0,0,0,.12)' } : undefined}
                      >
                        {l.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <label className={labelCls}>
                Template
                <select
                  className="nl-select"
                  value={tpl ?? ''}
                  disabled={!base}
                  onChange={(e) => load(lang ?? undefined, e.target.value)}
                >
                  {!tpl && <option value="">Loading…</option>}
                  {TEMPLATES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </label>
            </div>

            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))' }}>
              <label className={labelCls}>
                Customer mobile
                <input
                  className="nl-input font-mono"
                  value={phone}
                  inputMode="tel"
                  placeholder="077 123 4567"
                  aria-invalid={phoneBad}
                  onChange={(e) => { touchedTo.current = true; setPhone(e.target.value); }}
                  onBlur={() => { if (phoneE164) setPhone(phoneE164); }}
                />
                {phoneBad && <span className="text-[12px] font-medium" style={{ color: 'var(--warnFg)' }}>Not a valid number. WhatsApp and SMS open without a number.</span>}
                {!phoneBad && isSriLankaLandline(phoneE164) && <span className="text-[12px] font-medium" style={{ color: 'var(--warnFg)' }}>Landline number. WhatsApp and SMS may not reach it.</span>}
                {!phone.trim() && <span className="text-[12px] font-normal text-text3">No number: you choose the contact in WhatsApp or Messages.</span>}
              </label>
              <label className={labelCls}>
                Customer email
                <input
                  className="nl-input"
                  type="email"
                  value={email}
                  placeholder="name@example.com"
                  aria-invalid={emailBad}
                  onChange={(e) => { touchedTo.current = true; setEmail(e.target.value); }}
                />
                {emailBad && <span className="text-[12px] font-medium" style={{ color: 'var(--warnFg)' }}>Not a valid email. Your email app opens without a recipient.</span>}
              </label>
            </div>

            <label className={labelCls}>
              <span className="flex justify-between gap-2.5">
                Message
                {edited && (
                  <button type="button" className="text-[12px] font-semibold text-brand" onClick={() => load(lang ?? undefined, tpl ?? undefined)}>
                    Reset to template
                  </button>
                )}
              </span>
              <textarea
                className="nl-input !h-auto py-2.5 font-normal leading-normal"
                style={{ resize: 'vertical' }}
                rows={5}
                value={loading && !base ? '' : msg}
                placeholder={loading ? 'Loading message…' : ''}
                disabled={!base}
                onChange={(e) => setMsg(e.target.value)}
              />
            </label>

            <div className="flex items-center gap-2 rounded-[10px] border border-borderc bg-surface2 py-2 pl-3 pr-2">
              <span className="flex-1 truncate font-mono text-[13px] font-semibold text-brand" title={link}>{link || '—'}</span>
              <button type="button" className="nl-btn nl-btn--secondary nl-btn--xs h-[30px]" disabled={!link} onClick={copyLink}>Copy link</button>
            </div>

            <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))' }}>
              {actions.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  disabled={!ready}
                  onClick={a.run}
                  className={`flex h-11 items-center gap-2.5 rounded-[10px] border px-3.5 text-left text-[14px] font-semibold text-text1 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand ${a.on ? 'border-brand bg-brand-soft' : 'border-borderc2 bg-surface hover:bg-surface2'}`}
                >
                  <span aria-hidden className="grid h-[22px] w-[22px] flex-none place-items-center rounded-md text-[10px] font-bold text-white" style={{ background: a.bg }}>{a.glyph}</span>
                  {a.label}
                </button>
              ))}
            </div>

            {smsQr && (
              <div className="grid items-center gap-4 rounded-xl border border-brand bg-brand-soft p-3.5" style={{ gridTemplateColumns: '120px minmax(0,1fr)' }}>
                <div className="rounded-lg bg-white p-1.5">
                  <QRCode value={smsUrl(phoneE164, msg)} size={108} level="L" style={{ width: '100%', height: 'auto', display: 'block' }} aria-label="QR code that opens Messages with this text" />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="font-semibold text-text1">Scan to text from your phone</span>
                  <span className="text-[13px] text-text2">
                    {phoneE164
                      ? <>Opens your phone&apos;s messages app with the text and <span className="font-mono">{phoneE164}</span> filled in. Press send on the phone.</>
                      : <>Opens your phone&apos;s messages app with the text filled in. Choose the contact and press send.</>}
                  </span>
                  <button type="button" className="mt-1 self-start text-[13px] font-semibold text-brand" onClick={() => setSmsQr(false)}>Done</button>
                </div>
              </div>
            )}

            {/* Off-screen source for Download QR. */}
            <div ref={qrRef} aria-hidden style={{ position: 'fixed', left: -10000, top: 0 }}>
              {link && <QRCode value={link} size={512} level="M" />}
            </div>
          </div>

          <PreviewColumn
            wide={wide}
            pv={pv}
            setPv={setPv}
            shopName={shopName}
            accent={accent}
            number={number}
            msg={msg}
            link={link}
            sms={sms}
            subject={emailSubject}
            emailTo={isEmail(email) ? email.trim() : '—'}
            replyTo={shop?.email}
            isQuote={isQuote}
            amount={amountMinor != null ? money(amountMinor, currency) : '—'}
            due={date(isQuote ? doc.validUntil : doc.dueDate) || '—'}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2.5 border-t border-borderc px-6 py-3.5">
          <span className="min-w-[220px] flex-1 text-[13px] text-text2">Sharing from your device is logged on the invoice timeline.</span>
          <button type="button" className="nl-btn nl-btn--secondary" onClick={onClose}>Done</button>
        </div>
      </div>

      {toast && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 z-[1100] flex -translate-x-1/2 items-center gap-2 rounded-[10px] px-4 py-2.5 text-[14px] font-medium text-white"
          style={{ background: '#131318', boxShadow: 'var(--shadowLg)' }}
        >
          <span aria-hidden style={{ color: '#45D39F' }}>✓</span>{toast}
        </div>
      )}
    </div>
  );
}

const nowHm = () => new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Colombo' });

/** "On the customer's phone": always light, like the phone it imitates. */
function PreviewColumn({
  wide, pv, setPv, shopName, accent, number, msg, link, sms, subject, emailTo, replyTo, isQuote, amount, due,
}: {
  wide: boolean; pv: Pv; setPv: (p: Pv) => void; shopName: string; accent: string; number: string;
  msg: string; link: string; sms: ReturnType<typeof smsSegments>; subject?: string; emailTo: string;
  replyTo?: string; isQuote: boolean; amount: string; due: string;
}) {
  const body = withoutLink(msg, link);
  const time = nowHm();
  const mono = { fontFamily: "'JetBrains Mono', monospace" };
  const phoneFrame = 'flex min-h-[420px] flex-1 flex-col overflow-hidden rounded-[22px] border-[6px] border-[#1B1B22]';
  return (
    <div className={`flex min-w-0 flex-col gap-3 bg-surface2 px-5 py-[18px] ${wide ? 'border-l border-borderc' : 'border-t border-borderc'}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-[.08em] text-text3">On the customer&apos;s phone</span>
        <div role="tablist" aria-label="Preview" className="flex gap-0.5 rounded-lg border border-borderc bg-surface p-[3px]">
          {PREVIEWS.map((p) => (
            <button
              key={p.value}
              type="button"
              role="tab"
              aria-selected={pv === p.value}
              onClick={() => setPv(p.value)}
              className={`h-[26px] rounded-md px-[9px] text-[12px] font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand ${pv === p.value ? 'bg-brand-soft text-brand' : 'text-text2'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {pv === 'wa' && (
        <div className={phoneFrame} style={{ background: '#ECE5DD' }}>
          <div className="flex items-center gap-2.5 px-3 py-2.5 text-white" style={{ background: '#1F4D3F' }}>
            <span className="grid h-[30px] w-[30px] place-items-center rounded-full text-[11px] font-bold" style={{ background: accent }}>{initials(shopName)}</span>
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-[14px] font-semibold">{shopName}</span>
              <span className="text-[11px] opacity-80">Business account · via Netlise</span>
            </div>
          </div>
          <div className="flex flex-1 flex-col gap-2 px-3 py-3.5">
            <span className="self-center rounded-md px-2 py-[3px] text-[11px]" style={{ background: '#E1F0F7', color: '#3B4A54' }}>Today</span>
            <div className="max-w-[88%] self-start overflow-hidden bg-white" style={{ borderRadius: '2px 10px 10px 10px', boxShadow: '0 1px 1px rgba(0,0,0,.12)', color: '#111B21' }}>
              <div className="m-1 flex items-center gap-2.5 rounded-[7px] p-2.5" style={{ background: '#F5F6F6' }}>
                <span className="grid h-9 w-[30px] flex-none place-items-center rounded text-[9px] font-bold text-white" style={{ background: '#E0484D' }}>PDF</span>
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-[13px] font-semibold" style={mono}>{number}.pdf</span>
                  <span className="text-[11px]" style={{ color: '#667781' }}>PDF</span>
                </div>
              </div>
              <div className="whitespace-pre-wrap break-words px-2.5 pb-1.5 pt-1 text-[13.5px] leading-[1.45]">{body}</div>
              <div className="flex justify-between gap-2 px-2.5 pb-1.5 text-[11px]" style={{ color: '#667781' }}>
                <span className="truncate">{shopName} · Netlise</span><span>{time}</span>
              </div>
              <div className="border-t p-2.5 text-center text-[14px] font-semibold" style={{ borderColor: '#E9EDEF', color: '#0B7DA3' }}>
                ↗ {isQuote ? 'View quotation' : 'View and pay'}
              </div>
            </div>
          </div>
        </div>
      )}

      {pv === 'sms' && (
        <div className={phoneFrame} style={{ background: '#FFFFFF', color: '#131318' }}>
          <div className="flex flex-col items-center gap-1 border-b p-3" style={{ borderColor: '#EEE' }}>
            <span className="grid h-[34px] w-[34px] place-items-center rounded-full text-[11px] font-bold" style={{ background: '#D5D5DD', color: '#45454F' }}>{initials(shopName)}</span>
            <span className="text-[13px] font-semibold">{shopName}</span>
          </div>
          <div className="flex flex-1 flex-col gap-1.5 px-3 py-3.5">
            <span className="self-center text-[11px]" style={{ color: '#6A6A76' }}>Text message · Today {time}</span>
            <div className="max-w-[88%] self-start whitespace-pre-wrap break-words px-3 py-2.5 text-[14px] leading-[1.45]" style={{ background: '#E9E9EE', borderRadius: '16px 16px 16px 4px' }}>{msg}</div>
          </div>
          <div className="flex justify-between gap-2 border-t px-3 py-2.5 text-[12px] tabular-nums" style={{ borderColor: '#EEE', color: '#45454F' }}>
            <span>{sms.characters} characters · {sms.encoding === 'GSM7' ? 'Standard' : 'Unicode, 70 per segment'}</span>
            <span className="font-bold" style={{ color: sms.segments > 1 ? '#8A5208' : '#08724F' }}>
              {sms.segments} {sms.segments === 1 ? 'segment' : 'segments'}
            </span>
          </div>
        </div>
      )}

      {pv === 'email' && (
        <div className="flex min-h-[420px] flex-1 flex-col overflow-hidden rounded-xl border border-borderc2 bg-white" style={{ color: '#131318' }}>
          <div className="flex flex-col gap-[3px] border-b px-3.5 py-2.5 text-[12px]" style={{ background: '#F3F3F6', borderColor: '#E7E7EC' }}>
            <span className="text-[14px] font-semibold">{subject || `${isQuote ? 'Quotation' : 'Invoice'} ${number} from ${shopName}`}</span>
            <span style={{ color: '#45454F' }}>From {shopName} &lt;invoices@netlise.com&gt;</span>
            <span style={{ color: '#45454F' }}>To {emailTo}{replyTo ? ` · Reply to ${replyTo}` : ''}</span>
          </div>
          <div className="flex flex-1 flex-col gap-3.5 p-[18px]">
            <div className="flex items-center gap-2.5">
              <span className="grid h-8 w-8 place-items-center rounded-lg text-[12px] font-bold text-white" style={{ background: accent }}>{initials(shopName)}</span>
              <span className="font-bold">{shopName}</span>
            </div>
            <span className="whitespace-pre-wrap break-words text-[14px] leading-[1.5]" style={{ color: '#2A2A33' }}>{body}</span>
            <div className="grid grid-cols-2 gap-2 rounded-[10px] border p-3.5" style={{ borderColor: '#E7E7EC' }}>
              <div className="flex flex-col">
                <span className="text-[11px] font-semibold tracking-[.06em]" style={{ color: '#676773' }}>{isQuote ? 'QUOTATION TOTAL' : 'AMOUNT DUE'}</span>
                <span className="text-[18px] font-bold tabular-nums">{amount}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[11px] font-semibold tracking-[.06em]" style={{ color: '#676773' }}>{isQuote ? 'VALID UNTIL' : 'DUE DATE'}</span>
                <span className="text-[15px] font-semibold">{due}</span>
              </div>
            </div>
            <span className="self-start rounded-lg px-[22px] py-[11px] font-semibold text-white" style={{ background: accent }}>{isQuote ? 'View quotation' : 'Pay'}</span>
            <span className="flex items-center gap-2 self-start rounded-lg border px-2.5 py-1.5 text-[12px]" style={{ borderColor: '#E7E7EC' }}>
              <span className="grid h-5 w-5 place-items-center rounded text-[8px] font-bold text-white" style={{ background: '#E0484D' }}>PDF</span>
              <span className="font-semibold" style={mono}>{number}.pdf</span>
            </span>
            <span className="mt-auto text-[11px]" style={{ color: '#8A8A96' }}>Sent for {shopName} by Netlise</span>
          </div>
        </div>
      )}
    </div>
  );
}
